/**
 * @file /study/:familyId — 選んだ家族の学習の見取り図と、お子さんごとの記録を出す。
 *
 * @what: URL の familyId が caller の所属に含まれることを `GET /me/families` で確認し、
 *   上部に貼り付く切り替えバー (StudyTabs) で10の面を出し分ける。状態ボタン・
 *   「いまここ」・メモの変更は画面下の保存バーからまとめて
 *   `PUT /families/{fid}/study/plans/{personId}` する。開いている面は `?view=` に持つので、
 *   リーダーからの戻り先が本だなになる。
 * @why: 節を縦に積むと、いま何を見ているのか分からず目的の場所に届かない (代表の判断、2026-08-22)。
 *   1画面1ステップ + 領域カードにして8領域 × 4ステップの表の詰め込みをやめた
 *   方針 (2026-08-19) はそのまま、上の階層をタブで畳む。画面の文言は養育者が読む日常の
 *   日本語に揃え、内部方針は書かない。
 *   家族名と役割はヘッダーのメニューに渡し (useHeaderFamily)、本文には出さない。
 * @failure: 所属外の familyId は「この家族には所属していないか、見つかりません」+ 家族選択へのリンク。
 *   お子さんが居なければ見取り図だけを出し、アプリでの登録を案内する。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { familyLabel, roleLabel } from "@/features/study/api/families";
import { useHeaderFamily } from "@/shared/ui/headerContext";
import { useHeaderLearner } from "@/shared/ui/headerLearnerContext";
import { isAdultLearner, memberLabel, type FamilyMember } from "@/features/study/api/members";
import {
  isActiveMaterial,
  nextUnitStatus,
  type StudyMaterialInput,
  type StudyProgressLogInput,
} from "@/features/study/api/plans";
import { AnalysisPanel } from "@/features/study/components/AnalysisPanel";
import { READING_REALM_ID } from "@/features/study/model/realms";
import { BookshelfPanel } from "@/features/study/components/BookshelfPanel";
import { ReviewCardsPanel } from "@/features/study/components/ReviewCardsPanel";
import { AskRecordLinks } from "@/features/study/components/AskRecordLinks";
import { useReviewCards } from "@/features/study/hooks/useReviewCards";
import { ExplorationCard } from "@/features/study/components/ExplorationCard";
import { MaterialsPanel } from "@/features/study/components/MaterialsPanel";
import { MemoCard } from "@/features/study/components/MemoCard";
import { MilestonePanel } from "@/features/study/components/MilestonePanel";
import { MotorPanel } from "@/features/study/components/MotorPanel";
import { MusicPanel } from "@/features/study/components/MusicPanel";
import { ReadingPanel } from "@/features/study/components/ReadingPanel";
import { readLastLearner, writeLastLearner } from "@/features/study/lib/lastLearner";
import { LearningPlansPanel } from "@/features/study/components/LearningPlansPanel";
import type { RoadmapMaterial } from "@/features/study/components/roadmapTypes";
import { PreparationsCard } from "@/features/study/components/PreparationsCard";
import { ProgressLogCard } from "@/features/study/components/ProgressLogCard";
import { SaveBar } from "@/features/study/components/SaveBar";
import { SansuuFlowCard } from "@/features/study/components/SansuuFlowCard";
import { SodachiCard } from "@/features/study/components/SodachiCard";
import { StepPanel } from "@/features/study/components/StepPanel";
import { StepTabs } from "@/features/study/components/StepTabs";
import { StudyNotebook } from "@/features/study/components/StudyNotebook";
import { StudyTabs } from "@/features/study/components/StudyTabs";
import {
  ADULT_PLAN_DISPLAYS,
  ADULT_STUDY_VIEW_IDS,
  LEARNER_SCOPED_VIEW_IDS,
  LEGACY_VIEW_TO_PLAN_DISPLAY,
  PLAN_DISPLAYS,
  STUDY_VIEWS,
  isPlanDisplay,
  type PlanDisplay,
  type StudyViewId,
} from "@/features/study/components/studyViews";
import { TimePlanCard } from "@/features/study/components/TimePlanCard";
import { VoicePanel } from "@/features/study/components/VoicePanel";
import { useLearners } from "@/features/study/hooks/useLearners";
import { useStudyBackground } from "@/features/study/hooks/useStudyBackground";
import { useStudyCorpus } from "@/features/study/hooks/useStudyCorpus";
import { useStudyExploration } from "@/features/study/hooks/useStudyExploration";
import { useStudySchedule } from "@/features/study/hooks/useStudySchedule";
import { useStudySources } from "@/features/study/hooks/useStudySources";
import { shelfBooks } from "@/features/study/model/shelfBooks";
import { activityByIsbn } from "@/features/study/model/plannedRanges";
import { useReadingSessions } from "@/features/study/hooks/useReadingSessions";
import { addDaysLocal, todayLocal } from "@/features/study/api/dates";
import type { StudySource } from "@/features/study/api/sources";
import { useMyFamilies } from "@/features/study/hooks/useMyFamilies";
import { usePlannedBooks } from "@/features/study/hooks/usePlannedBooks";
import { useProgressLogs } from "@/features/study/hooks/useProgressLogs";
import { useReadingCatalogs } from "@/features/study/hooks/useReadingCatalogs";
import { useTextbookRoutes } from "@/features/study/hooks/useTextbookRoutes";
import { useStudyPlan } from "@/features/study/hooks/useStudyPlan";
import { attainmentModel } from "@/features/study/model";
import "@/features/study/components/study.css";

function isStudyView(v: string | null): v is StudyViewId {
  return STUDY_VIEWS.some((x) => x.id === v);
}

// 学習者が居ないときの既定値を使い回す。※なぜなら毎レンダー新しい配列にすると、
// 学習者に依存する useMemo が無駄に再計算されるためである。
const NO_LEARNERS: FamilyMember[] = [];
const NO_SOURCES: StudySource[] = [];

function FamilyStudyPage({ familyId }: { familyId: string }) {
  const model = attainmentModel;
  const [params, setParams] = useSearchParams();
  const viewParam = params.get("view");
  // 古い URL (?view=map / ?view=route) は計画の見せ方へ移す。view の値としては無効なので
  // isStudyView で落ち、計画 (plan) と見せ方の組に読み替える
  const legacyDisplay = viewParam !== null ? LEGACY_VIEW_TO_PLAN_DISPLAY[viewParam] : undefined;
  const view: StudyViewId = isStudyView(viewParam) ? viewParam : "plan";
  const displayParam = params.get("display");
  const display: PlanDisplay =
    view !== "plan"
      ? "step"
      : (legacyDisplay ?? (isPlanDisplay(displayParam) ? displayParam : "step"));
  // おとなの学習者を選んでいる間に計画を開いたら、受験の参考書の見せ方のうちスケジュールから始める
  // (代表の指示、2026-09-17。科目 × 難易度の表は本を選ぶときに見るもので、日々開くのはスケジュール)
  const adultRef = useRef(false);
  const setView = useCallback(
    (next: StudyViewId) => {
      const p = new URLSearchParams(params);
      if (next === "plan") p.delete("view");
      else p.set("view", next);
      p.delete("display");
      if (next === "plan" && adultRef.current) p.set("display", "schedule");
      setParams(p, { replace: true });
      // 面を切り替えたら先頭へ戻す。切り替えのバーは貼り付いたままなので、戻さないと
      // 前の面のスクロール位置のまま次の面の途中が出てしまう。
      if (typeof window.scrollTo === "function") window.scrollTo({ top: 0 });
    },
    [params, setParams],
  );

  /**
   * 計画の見せ方を切り替える。
   *
   * @what: `?display=` を書き換え、view は計画 (既定なので消す) にし、先頭へ戻す。いまのステップは
   *   既定なので URL から消す。
   * @why: 見取り図と参考書ルートを計画の中の見せ方にしたので (代表の判断、2026-09-06)、面の切り替え
   *   (setView) と同じく URL に持ち、リンクで共有できるようにする。
   */
  const setDisplay = useCallback(
    (next: PlanDisplay) => {
      const p = new URLSearchParams(params);
      p.delete("view");
      if (next === "step") p.delete("display");
      else p.set("display", next);
      setParams(p, { replace: true });
      if (typeof window.scrollTo === "function") window.scrollTo({ top: 0 });
    },
    [params, setParams],
  );

  const learnersState = useLearners(familyId);
  const [selectedLearnerId, setSelectedLearnerId] = useState<string | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<string>(model.stages[0]?.id ?? "s0");

  // @what: 端末が覚えているまなぶ人を選び直し、覚えが無ければ一覧の先頭を選ぶ。
  // @why: 選んだ人を覚えないと、読み込み直すたびに先頭の人へ戻る (代表の指摘、2026-09-07)。
  // @failure: 覚えている人が家族から外れていれば先頭に倒す (覚えは家族ごとの localStorage)。
  useEffect(() => {
    if (learnersState.status !== "ready" || selectedLearnerId !== null) return;
    const remembered = readLastLearner(familyId);
    const found =
      remembered !== null && learnersState.learners.some((l) => l.person_id === remembered)
        ? remembered
        : (learnersState.learners[0]?.person_id ?? null);
    if (found !== null) setSelectedLearnerId(found);
  }, [learnersState, selectedLearnerId, familyId]);

  // ヘッダーで選び直したら端末に覚えさせる (記録シートなどの既定と同じ覚えを使う)。
  const selectLearner = useCallback(
    (personId: string) => {
      setSelectedLearnerId(personId);
      writeLastLearner(familyId, personId);
    },
    [familyId],
  );

  const plan = useStudyPlan(familyId, selectedLearnerId);
  const planReady = selectedLearnerId !== null && plan.state.status === "ready";
  const progressLogs = useProgressLogs(familyId, planReady ? selectedLearnerId : null);
  // 家族単位の参照情報 (未同期 = null。面の中で「まだ届いていない」と案内する)。
  const exploration = useStudyExploration(familyId);
  const schedule = useStudySchedule(familyId);
  const sourcesState = useStudySources(familyId);
  // 計画に出てくる本が本だなにあるか (ISBN で突き合わせる)。表紙の左下の印に使う。
  const shelf = useMemo(
    () => shelfBooks(familyId, sourcesState.status === "ready" ? sourcesState.sources : null),
    [familyId, sourcesState],
  );
  // 復習カードは本だなの面でだけ読む (きょうの復習を出すため)。
  const reviewCards = useReviewCards(familyId, view === "shelf");
  // 本だなの「取り組むもの」と「教養」の分かれ目は、選んでいる人の計画から決める。
  const plannedBooks = usePlannedBooks(familyId, selectedLearnerId, view === "shelf");
  // よむタブを開くまで候補一覧を読まない (250冊の JSON を他の面で持ち回らない)。
  const readingCatalogs = useReadingCatalogs(familyId, view === "reading");
  // 参考書ルートのタブを開くまで読まない (科目ごとの本の入れ子を他の面で持ち回らない)。
  const routeDisplay = view === "plan" && (display === "grid" || display === "schedule");
  const textbookRoutes = useTextbookRoutes(familyId, routeDisplay);
  // スケジュールの面でだけ、この1年に読んだ時間を読み、本 (ISBN) ごとの触れた日にまとめる。
  // 手書きの記録は上で読んでいる直近の一覧を使う (計画の教材か本だなの資料に結べるものだけ)。
  const [activityToday] = useState(todayLocal);
  const activityFrom = useMemo(() => addDaysLocal(activityToday, -365), [activityToday]);
  const readingSessions = useReadingSessions(
    familyId,
    selectedLearnerId,
    activityFrom,
    activityToday,
    view === "plan" && display === "schedule",
  );
  const planState = plan.state;
  const progressLogsState = progressLogs.state;
  const activity = useMemo(
    () =>
      readingSessions.status === "ready"
        ? activityByIsbn(
            readingSessions.sessions,
            progressLogsState.status === "ready" ? progressLogsState.items : [],
            sourcesState.status === "ready" ? sourcesState.sources : [],
            planState.status === "ready" ? (planState.plan?.materials ?? []) : [],
          )
        : undefined,
    [readingSessions, progressLogsState, sourcesState, planState],
  );
  // 出題を使う面を開くまで読まない (見取り図は「終わりまでにできていてほしいこと」の出題数に使う)。
  const corpus = useStudyCorpus(
    familyId,
    sourcesState.status === "ready" ? sourcesState.sources : null,
    view === "plan" || view === "shelf" || view === "analysis",
  );

  // お子さんの plan が読み込めたら、そのお子さんの「いまここ」のステップを開く。
  const loadedStageId =
    plan.state.status === "ready" ? plan.state.plan?.current_stage_id : undefined;
  useEffect(() => {
    if (plan.state.status !== "ready") return;
    if (loadedStageId && model.stages.some((s) => s.id === loadedStageId)) {
      setSelectedStageId(loadedStageId);
    }
  }, [plan.state.status, loadedStageId, model.stages, selectedLearnerId]);

  const learners = learnersState.status === "ready" ? learnersState.learners : NO_LEARNERS;
  const selectedLearner = learners.find((l) => l.person_id === selectedLearnerId) ?? null;
  const stage = model.stages.find((s) => s.id === selectedStageId) ?? model.stages[0]!;

  // いま操作している学ぶ人はヘッダーの LearnerSwitcher に出す (本文のタブに埋もれさせない)。
  const learnerOptions = useMemo(
    () => learners.map((l) => ({ id: l.person_id, label: memberLabel(l) })),
    [learners],
  );
  useHeaderLearner(
    learners.length > 0
      ? { options: learnerOptions, selectedId: selectedLearnerId, onSelect: selectLearner }
      : null,
  );

  // おとな (養育者) の学習者を選んでいる間は、面を本だなだけに絞る (epic_23 §10。
  // 親の学習は自炊した本が起点で、子ども向けの面と混ぜない)。ほかの面を開いたまま
  // 切り替えたときは本だなへ移す。
  const selectedIsAdult = selectedLearner !== null && isAdultLearner(selectedLearner);
  const views = selectedIsAdult
    ? STUDY_VIEWS.filter((v) => ADULT_STUDY_VIEW_IDS.includes(v.id))
    : STUDY_VIEWS;
  // おとなの学習者を選んでいる間は、面を計画 (受験の参考書の見せ方だけ) と本だなに絞る。
  // 子ども向けの面や計画のステップの見せ方を開いたまま切り替えたときは本だなへ移す
  useEffect(() => {
    adultRef.current = selectedIsAdult;
  }, [selectedIsAdult]);
  useEffect(() => {
    if (!selectedIsAdult) return;
    if (
      !ADULT_STUDY_VIEW_IDS.includes(view) ||
      (view === "plan" && !ADULT_PLAN_DISPLAYS.includes(display))
    )
      setView("shelf");
  }, [selectedIsAdult, view, display, setView]);
  const planDisplays = selectedIsAdult
    ? PLAN_DISPLAYS.filter((d) => ADULT_PLAN_DISPLAYS.includes(d.id))
    : PLAN_DISPLAYS;

  const toggleExit = (exitId: string) => {
    const exitStates = { ...plan.draft.exitStates };
    if (exitStates[exitId]) {
      delete exitStates[exitId];
    } else {
      exitStates[exitId] = true;
    }
    plan.setDraft({ ...plan.draft, exitStates });
  };

  // お気に入りは押した順に後ろへ足す (よく使う順でなく、登録順のほうが並びが動かず選びやすい)
  const toggleMotorFavorite = (itemId: string) => {
    const current = plan.draft.motorFavorites;
    const motorFavorites = current.includes(itemId)
      ? current.filter((id) => id !== itemId)
      : [...current, itemId];
    plan.setDraft({ ...plan.draft, motorFavorites });
  };

  const togglePrep = (prepId: string) => {
    const prepStates = { ...plan.draft.prepStates };
    if (prepStates[prepId]) {
      delete prepStates[prepId];
    } else {
      prepStates[prepId] = true;
    }
    plan.setDraft({ ...plan.draft, prepStates });
  };

  // 単元 → 紐づいた教材 (見取り図のマスと行の先頭) と、ステップだけ決めた教材、
  // このステップで教材が決まっていない単元 (水準が「触れる」= 教材を使わない単元は除く)。
  // draft を見るので保存前から反映される。
  const materialsByUnit: Record<string, RoadmapMaterial[]> = {};
  const materialsByStage: Record<string, RoadmapMaterial[]> = {};
  for (const m of plan.draft.materials) {
    // 候補から外した教材は見取り図に出さない (教材の一覧には残る)
    if (!isActiveMaterial(m)) continue;
    const entry: RoadmapMaterial = {
      id: m.id,
      title: m.title,
      status: m.status,
      ...(m.stage_id ? { stage_id: m.stage_id } : {}),
      ...(m.memo ? { memo: m.memo } : {}),
    };
    for (const uid of m.unit_ids) {
      (materialsByUnit[uid] ??= []).push(entry);
    }
    if (m.unit_ids.length === 0 && m.stage_id) {
      (materialsByStage[m.stage_id] ??= []).push(entry);
    }
  }
  // 取り込んだ資料での単元ごとの出題数 (終わりまでにできていてほしいことの横に出す)
  const questionCountByUnit =
    corpus.status === "ready"
      ? Object.fromEntries(
          Object.entries(corpus.corpus.byUnitId).map(([id, entries]) => [id, entries.length]),
        )
      : undefined;

  const toggleUnit = (unitId: string) => {
    const current = plan.draft.unitStates[unitId];
    const next = nextUnitStatus(current?.status);
    const unitStates = { ...plan.draft.unitStates };
    if (next === "not_started" && !current?.note) {
      delete unitStates[unitId];
    } else {
      unitStates[unitId] = current?.note ? { status: next, note: current.note } : { status: next };
    }
    plan.setDraft({ ...plan.draft, unitStates });
  };

  const showSaveBar = planReady;
  // お子さんの選択が必要な面
  const learnerScoped = LEARNER_SCOPED_VIEW_IDS.includes(view) && !routeDisplay;
  const sourceHrefBase = `/study/${encodeURIComponent(familyId)}/sources`;

  return (
    <div
      className={showSaveBar && (plan.dirty || plan.saving) ? "study-page--with-bar" : undefined}
    >
      <StudyTabs selectedId={view} onSelect={setView} views={views} />

      {/* まなぶ人の切り替えはヘッダー (LearnerSwitcher) が持つ。本文には読み込み中と
          読み込めなかったときの案内だけを残す。本だなは plan を読まないので、plan の
          読み込み失敗もここでは案内しない。 */}
      {learnerScoped ? (
        learnersState.status === "loading" ? (
          <div role="status" className="study-muted" style={{ marginBottom: "var(--space-4)" }}>
            お子さんの情報を読み込んでいます...
          </div>
        ) : learnersState.status === "error" ? (
          <p role="alert" className="study-error">
            お子さんの情報を読み込めませんでした。時間を置いて再読み込みしてください。
          </p>
        ) : learners.length === 0 ? (
          <p className="study-muted">
            お子さんが登録されていません。いちご手帳アプリでお子さんを追加すると、ここで学習の記録を
            つけられます。
          </p>
        ) : plan.state.status === "error" ? (
          <p role="alert" className="study-error">
            記録を読み込めませんでした。時間を置いて再読み込みしてください。
          </p>
        ) : selectedLearnerId !== null && plan.state.status === "loading" ? (
          <p role="status" className="study-muted">
            読み込み中...
          </p>
        ) : null
      ) : null}

      <div
        role="tabpanel"
        id={`study-view-panel-${view}`}
        aria-labelledby={`study-view-tab-${view}`}
      >
        {view === "plan" ? (
          <div
            className="study-route-display study-plan-display"
            role="group"
            aria-label="計画の見せ方"
          >
            {planDisplays.map((d) => (
              <button
                key={d.id}
                type="button"
                className="study-ghost-btn"
                aria-pressed={display === d.id}
                onClick={() => setDisplay(d.id)}
              >
                {d.label}
              </button>
            ))}
          </div>
        ) : null}

        {view === "plan" && display === "step" ? (
          <>
            <div className="study-step-tabs-and-panel">
              <StepTabs
                stages={model.stages}
                selectedId={stage.id}
                {...(planReady && plan.draft.currentStageId
                  ? { currentStageId: plan.draft.currentStageId }
                  : {})}
                onSelect={setSelectedStageId}
              />
              <StepPanel
                model={model}
                stage={stage}
                isCurrent={planReady && plan.draft.currentStageId === stage.id}
                sourceHrefBase={sourceHrefBase}
                {...(corpus.status === "ready" ? { corpusByUnit: corpus.corpus.byUnitId } : {})}
                preparationSlot={
                  <PreparationsCard
                    preparations={stage.preparations}
                    {...(planReady
                      ? { prepStates: plan.draft.prepStates, onToggle: togglePrep }
                      : {})}
                  />
                }
                {...(planReady
                  ? {
                      unitStates: plan.draft.unitStates,
                      onToggleUnit: toggleUnit,
                      onMakeCurrent: () =>
                        plan.setDraft({ ...plan.draft, currentStageId: stage.id }),
                      materialsByUnit,
                    }
                  : {})}
              />
            </div>

            <SodachiCard
              sources={sourcesState.status === "ready" ? sourcesState.sources : null}
              sourceHrefBase={sourceHrefBase}
              onOpenView={(v) => (v === "map" ? setDisplay("map") : setView(v))}
            />

            {planReady ? (
              <p className="study-crosslink">
                教材の登録と単元への紐づけは
                <button
                  type="button"
                  className="study-link-btn"
                  onClick={() => setView("materials")}
                >
                  「教材」タブ
                </button>
                で行います。
              </p>
            ) : null}
          </>
        ) : null}

        {view === "plan" && display === "map" ? (
          <section className="study-section">
            <h2 className="study-section-title">
              <span aria-hidden="true">📍</span> 単元 × ステップ
            </h2>
            <p className="study-section-help">
              どの単元を、いつ・どこまで・何の教材で進めるかを1枚で見ます。
            </p>
            <details className="study-howto">
              <summary>この表の読み方</summary>
              <ul>
                <li>
                  行は単元、列はステップ。マスの印は、そのステップで目指す水準（触れる → 導入 → 練習
                  → 仕上げ）。
                </li>
                <li>
                  マスには、そのステップ・その単元で使う教材が登録順に並ぶ。教材の「使う時期」と「単元を選ぶ」で決める。
                </li>
                <li>行の先頭の状態ボタンで 未着手 → 取り組み中 → できた を切り替える。</li>
                <li>
                  「チェックテスト」は初めて見る問題で解ける割合を見る回、「体験のふり返り」は同年代の子と過ごす場や体を動かす場が足りているかを見る回を指します。
                </li>
              </ul>
            </details>
            <MilestonePanel
              model={model}
              {...(planReady && plan.draft.currentStageId
                ? { currentStageId: plan.draft.currentStageId }
                : {})}
              {...(planReady
                ? {
                    unitStates: plan.draft.unitStates,
                    onToggleUnit: toggleUnit,
                    onMakeCurrent: (stageId: string) =>
                      plan.setDraft({ ...plan.draft, currentStageId: stageId }),
                    prepStates: plan.draft.prepStates,
                    onTogglePrep: togglePrep,
                    exitStates: plan.draft.exitStates,
                    onToggleExit: toggleExit,
                    materialsByUnit,
                    materialsByStage,
                    materials: plan.draft.materials,
                    onChangeMaterials: (materials: StudyMaterialInput[]) =>
                      plan.setDraft({ ...plan.draft, materials }),
                    onAddLog: async (input: StudyProgressLogInput) => {
                      const created = await progressLogs.add(input);
                      if (created?.to_page) {
                        plan.applyMaterialPageBump(created.material_id ?? "", created.to_page);
                      }
                      return created;
                    },
                    logBusy: progressLogs.busy,
                    logError: progressLogs.actionError,
                    onOpenMaterialsTab: () => setView("materials"),
                    shelf,
                    ...(progressLogs.state.status === "ready"
                      ? { logs: progressLogs.state.items }
                      : {}),
                    ...(plan.state.status === "ready" && plan.state.plan
                      ? {
                          serverUnitStates: plan.state.plan.unit_states,
                          serverExitStates: plan.state.plan.exit_states ?? {},
                        }
                      : {}),
                  }
                : {})}
              {...(corpus.status === "ready" ? { corpusByUnit: corpus.corpus.byUnitId } : {})}
              sourceHrefBase={sourceHrefBase}
              {...(questionCountByUnit ? { questionCountByUnit } : {})}
              {...(planReady && selectedLearner
                ? { learnerName: memberLabel(selectedLearner) }
                : {})}
            />
            {planReady ? (
              <p className="study-crosslink">
                教材の登録と単元への紐づけは
                <button
                  type="button"
                  className="study-link-btn"
                  onClick={() => setView("materials")}
                >
                  「教材」タブ
                </button>
                で行います。
              </p>
            ) : null}
          </section>
        ) : null}

        {view === "plan" && display === "map" ? (
          <SansuuFlowCard
            sources={sourcesState.status === "ready" ? sourcesState.sources : null}
            sourceHrefBase={sourceHrefBase}
          />
        ) : null}

        {view === "motor" ? (
          <MotorPanel
            familyId={familyId}
            favorites={plan.draft.motorFavorites}
            onToggleFavorite={toggleMotorFavorite}
            sourceHrefBase={sourceHrefBase}
            planReady={planReady}
            hasLearners={learners.length > 0}
            sources={sourcesState.status === "ready" ? sourcesState.sources : null}
            logsState={progressLogs.state}
            onAdd={(input) => progressLogs.add(input)}
            onRemove={(id) => progressLogs.remove(id)}
            busy={progressLogs.busy}
            actionError={progressLogs.actionError}
          />
        ) : null}

        {view === "music" ? (
          <MusicPanel
            familyId={familyId}
            learnerPersonId={planReady ? selectedLearnerId : null}
            sourceHrefBase={sourceHrefBase}
            planReady={planReady}
            hasLearners={learners.length > 0}
            materials={plan.draft.materials}
            exploration={exploration.status === "ready" ? exploration.exploration : null}
            logsState={progressLogs.state}
            onAdd={(input) => progressLogs.add(input)}
            onRemove={(id) => void progressLogs.remove(id)}
            busy={progressLogs.busy}
            actionError={progressLogs.actionError}
          />
        ) : null}

        {view === "reading" ? (
          <ReadingPanel
            familyId={familyId}
            planReady={planReady}
            hasLearners={learners.length > 0}
            catalogsState={readingCatalogs}
            logsState={progressLogs.state}
            onAdd={(input) => progressLogs.add(input)}
            onRemove={(id) => void progressLogs.remove(id)}
            onLoadMore={() => void progressLogs.loadMore()}
            busy={progressLogs.busy}
            actionError={progressLogs.actionError}
          />
        ) : null}

        {view === "materials" ? (
          <section className="study-section">
            <h2 className="study-section-title">
              <span aria-hidden="true">📚</span> 教材
            </h2>
            <p className="study-section-help">
              持っている教材と買う候補を、使う時期ごとに表紙つきで見ます。
            </p>
            {planReady ? (
              <MaterialsPanel
                model={model}
                materials={plan.draft.materials}
                onChange={(materials) => plan.setDraft({ ...plan.draft, materials })}
                {...(plan.draft.currentStageId
                  ? { currentStageId: plan.draft.currentStageId }
                  : {})}
                {...(progressLogs.state.status === "ready"
                  ? { logs: progressLogs.state.items }
                  : {})}
                onAddLog={async (input) => {
                  const created = await progressLogs.add(input);
                  if (created?.to_page)
                    plan.applyMaterialPageBump(created.material_id ?? "", created.to_page);
                  return created;
                }}
                logBusy={progressLogs.busy}
                logError={progressLogs.actionError}
              />
            ) : learners.length > 0 ? (
              <p className="study-muted">画面の上でまなぶ人を選ぶと教材が出ます。</p>
            ) : null}
          </section>
        ) : null}

        {view === "log" ? (
          planReady ? (
            <>
              <section className="study-section">
                <h2 className="study-section-title">
                  <span aria-hidden="true">✍️</span> 取り組みの記録
                </h2>
                <p className="study-section-help">
                  その日の取り組みを残します。ページを入れると、教材の「いまのページ」も一緒に進みます。
                </p>
                <ProgressLogCard
                  familyId={familyId}
                  materials={plan.draft.materials}
                  logsState={
                    progressLogs.state.status === "ready"
                      ? {
                          ...progressLogs.state,
                          items: progressLogs.state.items.filter(
                            (l) =>
                              !l.motor_item_id &&
                              l.realm !== "music" &&
                              l.realm !== READING_REALM_ID,
                          ),
                        }
                      : progressLogs.state
                  }
                  onAdd={async (input) => {
                    const created = await progressLogs.add(input);
                    if (created?.material_id && created.to_page) {
                      plan.applyMaterialPageBump(created.material_id, created.to_page);
                    }
                    return created;
                  }}
                  onRemove={(id) => void progressLogs.remove(id)}
                  onLoadMore={() => void progressLogs.loadMore()}
                  busy={progressLogs.busy}
                  actionError={progressLogs.actionError}
                />
              </section>

              {selectedLearner ? (
                <section className="study-section">
                  <h2 className="study-section-title">
                    <span aria-hidden="true">📝</span> メモ
                  </h2>
                  <MemoCard
                    learnerName={memberLabel(selectedLearner)}
                    value={plan.draft.note}
                    onChange={(note) => plan.setDraft({ ...plan.draft, note })}
                  />
                </section>
              ) : null}
            </>
          ) : null
        ) : null}

        {view === "time" ? (
          <>
            <section className="study-section">
              <h2 className="study-section-title">
                <span aria-hidden="true">⚖️</span> 時間のつかいかた
              </h2>
              {schedule.status === "error" ? (
                <p role="alert" className="study-error">
                  時間のつかいかたを読み込めませんでした。時間を置いて再読み込みしてください。
                </p>
              ) : schedule.status === "ready" && schedule.schedule ? (
                <>
                  <p className="study-section-help">
                    カレンダーに置いた予定を、時間の計画としてまとめたものです。
                  </p>
                  <TimePlanCard
                    schedule={schedule.schedule}
                    model={model}
                    currentStageId={
                      planReady && plan.draft.currentStageId ? plan.draft.currentStageId : null
                    }
                  />
                </>
              ) : schedule.status === "ready" ? (
                <p className="study-muted">
                  カレンダーがまだ届いていません。予定を入れると、時間の計画としてここにまとまります。
                </p>
              ) : (
                <p role="status" className="study-muted">
                  読み込み中...
                </p>
              )}
            </section>

            <section className="study-section">
              <h2 className="study-section-title">
                <span aria-hidden="true">🧭</span> 習い事・体験
              </h2>
              {exploration.status === "error" ? (
                <p role="alert" className="study-error">
                  習い事・体験の情報を読み込めませんでした。時間を置いて再読み込みしてください。
                </p>
              ) : exploration.status === "ready" && exploration.exploration ? (
                <>
                  <p className="study-section-help">
                    いま通っているものと、次にむけて動いていることが And Lives
                    の「道すじ」から届きます。
                  </p>
                  <ExplorationCard exploration={exploration.exploration} />
                </>
              ) : exploration.status === "ready" ? (
                <p className="study-muted">習い事・体験の情報はまだ届いていません。</p>
              ) : (
                <p role="status" className="study-muted">
                  読み込み中...
                </p>
              )}
            </section>
          </>
        ) : null}

        {view === "analysis" ? (
          <section className="study-section">
            <h2 className="study-section-title">
              <span aria-hidden="true">🔍</span> 分析
            </h2>
            <p className="study-section-help">
              本だなの「計画のもと」を読み解いた結果です。入試に合わせるためではなく、いまの計画に
              足りないところを見つけるために読みます。
            </p>
            <AnalysisPanel model={model} corpusState={corpus} sourceHrefBase={sourceHrefBase} />
          </section>
        ) : null}

        {routeDisplay ? (
          <LearningPlansPanel
            routesState={textbookRoutes}
            display={display === "schedule" ? "schedule" : "grid"}
            familyId={familyId}
            learnerPersonId={selectedLearnerId}
            learnerLabel={selectedLearner ? memberLabel(selectedLearner) : "学習者"}
            sources={sourcesState.status === "ready" ? sourcesState.sources : null}
            showCareers={selectedIsAdult}
            {...(activity ? { activity } : {})}
          />
        ) : null}

        {view === "notes" ? (
          <section className="study-section">
            <h2 className="study-section-title">
              <span aria-hidden="true">📝</span> 学びメモ
            </h2>
            <StudyNotebook
              familyId={familyId}
              learnerId={selectedLearnerId}
              learners={learners}
            />
          </section>
        ) : null}

        {view === "shelf" ? (
          <section className="study-section">
            <h2 className="study-section-title">
              <span aria-hidden="true">📖</span> 本だな
            </h2>
            <p className="study-section-help">
              スキャンして取り込んだものが並びます。画面の上で選んでいる人の計画のカリキュラムに載っている
              本が「取り組むもの」、計画を立てるときに見るものが「計画のもと」、それ以外が「教養」です。
              選ぶ人を替えると、並びも変わります。
            </p>
            <AskRecordLinks familyId={familyId} learnerPersonId={selectedLearnerId} />
            <ReviewCardsPanel
              familyId={familyId}
              learnerPersonId={selectedLearnerId}
              sources={sourcesState.status === "ready" ? sourcesState.sources : NO_SOURCES}
              state={reviewCards.state}
              onAnswer={reviewCards.answer}
              onRetire={(card) =>
                reviewCards
                  .save(card.id, {
                    learner_person_id: card.learner_person_id,
                    prompt: card.prompt,
                    ...(card.answer ? { answer: card.answer } : {}),
                    ...(card.topic ? { topic: card.topic } : {}),
                    ...(card.ref ? { ref: card.ref } : {}),
                    status: "retired",
                  })
                  .then(() => undefined)
              }
              busy={reviewCards.busy}
              actionError={reviewCards.actionError}
            />
            <BookshelfPanel
              familyId={familyId}
              sourcesState={sourcesState}
              corpusState={corpus}
              learnerPersonId={selectedLearnerId}
              plannedState={plannedBooks}
            />
          </section>
        ) : null}

        {/* 声かけは読み物なので、家族の情報も選んでいる人も使わない。 */}
        {view === "voice" ? <VoicePanel /> : null}
      </div>

      {showSaveBar ? (
        <SaveBar
          dirty={plan.dirty}
          saving={plan.saving}
          saveError={plan.saveError}
          savedAt={plan.savedAt}
          onSave={() => void plan.save()}
        />
      ) : null}
    </div>
  );
}

export function StudyFamilyRoute() {
  const { familyId } = useParams<{ familyId: string }>();
  const state = useMyFamilies();
  useStudyBackground();
  // 家族名と役割はヘッダーのメニューに出す (本文には出さない)。
  const family =
    state.status === "ready" ? state.families.find((f) => f.family_id === familyId) : undefined;
  useHeaderFamily(
    family
      ? {
          label: familyLabel(family),
          role: roleLabel(family.role),
          switchable: state.status === "ready" && state.families.length > 1,
        }
      : null,
  );

  if (state.status === "loading") {
    return (
      <div
        className="study-scope"
        role="status"
        aria-live="polite"
        style={{ padding: "var(--space-5)" }}
      >
        読み込み中...
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <section className="study-scope study-page">
        <h1>いちごスタディ</h1>
        <p role="alert" className="study-error">
          家族の情報を読み込めませんでした。時間を置いて再読み込みしてください。
        </p>
      </section>
    );
  }

  if (!family || !familyId) {
    return (
      <section className="study-scope study-page">
        <h1>いちごスタディ</h1>
        <p role="alert" className="study-muted">
          この家族には所属していないか、見つかりません。
        </p>
        <Link to="/study">家族を選び直す</Link>
      </section>
    );
  }

  return (
    <section className="study-scope study-page">
      <h1 className="study-visually-hidden">いちごスタディ</h1>
      <FamilyStudyPage familyId={familyId} />
    </section>
  );
}
